import React, { useState, useEffect } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Paper,
  Alert,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import {
  updateFabrica,
  getFabricasTipo,
} from "../../api/ProveedoresyFabricas/fabricasApi";

import ProveedoresFabricasDocumentos from "./ProveedoresFabricasDocumentos";

const ACCENT = "#ffac7e";
const ACCENT_HOVER = "#e89566";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 2 },
};

const ProveedoresFabricasDrawer = ({ open, onClose, fabrica, onSave }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    id_Fabrica: "",
    NombreFabrica: "",
    Direccion: "",
    Pais: "",
    Telefono: "",
    Email: "",
    Notas: "",
    id_FabricaTipo: "",
  });
  const [tiposFabrica, setTiposFabrica] = useState([]);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (fabrica) {
      setFormData({
        id_Fabrica: fabrica.id_Fabrica || "",
        NombreFabrica: fabrica.NombreFabrica || "",
        Direccion: fabrica.Direccion || "",
        Pais: fabrica.Pais || "",
        Telefono: fabrica.Telefono || "",
        Email: fabrica.Email || "",
        Notas: fabrica.Notas || "",
        id_FabricaTipo: fabrica.id_FabricaTipo || "",
      });
    }
  }, [fabrica]);

  useEffect(() => {
    const fetchTipos = async () => {
      const tipos = await getFabricasTipo();
      setTiposFabrica(tipos);
    };
    fetchTipos();
  }, []);

  useEffect(() => {
    if (open) setFormError("");
  }, [open]);

  const handleChange = (e) => {
    setFormError("");
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    try {
      const updatedFabrica = await updateFabrica(formData);
      onSave(updatedFabrica);
      onClose();
    } catch (error) {
      console.error("Error al actualizar fábrica:", error);
      setFormError(t("ProveedoresPage.errorSaveFactory"));
    }
  };

  const btnPrimarySx = {
    py: 1.1,
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 2,
    bgcolor: ACCENT,
    color: theme.palette.getContrastText(ACCENT),
    boxShadow: "none",
    "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          boxShadow: `-12px 0 40px ${alpha(theme.palette.common.black, 0.12)}`,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          background: `linear-gradient(125deg, ${alpha(ACCENT, 0.28)} 0%, ${alpha(
            theme.palette.background.paper,
            1,
          )} 55%, ${alpha(theme.palette.divider, 0.06)} 100%)`,
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor: alpha(ACCENT, 0.45),
                color: theme.palette.text.primary,
              }}
            >
              <FactoryOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  color: "text.secondary",
                  lineHeight: 1.2,
                }}
              >
                {t("ProveedoresPage.factoriesTitle")}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.3, mt: 0.25 }}>
                {t("ProveedoresPage.drawerEditFactoryTitle")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {t("ProveedoresPage.drawerEditFactorySubtitle")}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("ProveedoresPage.closePanel")}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              borderRadius: 2,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2 }}>
        {formError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setFormError("")}>
            {formError}
          </Alert>
        ) : null}

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            borderColor: alpha(theme.palette.divider, 0.85),
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Stack spacing={2}>
            <TextField
              label={t("ProveedoresPage.labelFactoryName")}
              name="NombreFabrica"
              value={formData.NombreFabrica}
              onChange={handleChange}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t("ProveedoresPage.labelFactoryAddress")}
              name="Direccion"
              value={formData.Direccion}
              onChange={handleChange}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t("ProveedoresPage.labelFactoryCountry")}
              name="Pais"
              value={formData.Pais}
              onChange={handleChange}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t("ProveedoresPage.labelFactoryPhone")}
              name="Telefono"
              value={formData.Telefono}
              onChange={handleChange}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t("ProveedoresPage.labelFactoryEmail")}
              name="Email"
              value={formData.Email}
              onChange={handleChange}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t("ProveedoresPage.labelFactoryNotes")}
              name="Notas"
              value={formData.Notas}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={2}
              maxRows={10}
              sx={fieldSx}
            />
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel id="fabrica-tipo-edit-label">{t("ProveedoresPage.labelFactoryType")}</InputLabel>
              <Select
                labelId="fabrica-tipo-edit-label"
                name="id_FabricaTipo"
                value={formData.id_FabricaTipo || ""}
                label={t("ProveedoresPage.labelFactoryType")}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, id_FabricaTipo: e.target.value }))
                }
              >
                {tiposFabrica.map((tipo) => (
                  <MenuItem key={tipo.id_FabricaTipo} value={tipo.id_FabricaTipo}>
                    {tipo.NombreTipo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        <Box sx={{ mt: 2 }}>
          <ProveedoresFabricasDocumentos fabrica={fabrica} />
        </Box>
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          bgcolor: alpha(theme.palette.background.default, 0.65),
        }}
      >
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              flex: 1,
              py: 1.1,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              borderColor: alpha(theme.palette.text.primary, 0.2),
            }}
          >
            {t("ProveedoresPage.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSave} sx={{ flex: 1, ...btnPrimarySx }}>
            {t("ProveedoresPage.saveChanges")}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default ProveedoresFabricasDrawer;
